/**
 * Monthly allowances: the per-person spend limits admins set, and where each
 * person stands this month. The API's hourly check alerts at 50% and 80% and,
 * when switched on, blocks at 100%. With alert emails on, it also emails the
 * person (only) at 50%, 80% and when paused, in percentages.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { Panel, SubHeading } from '../components/Panel';

type Mode = 'off' | 'dry_run' | 'on';

interface Config {
  mode: Mode;
  opus_claude_usd: number;
  sonnet_claude_usd: number;
  codex_usd: number;
  alert_emails: boolean;
  updated_by: string;
  updated_at: string | null;
  last_run_at: string | null;
  last_run_error: string | null;
  last_run_summary: { blocked?: string[]; over?: string[] } | null;
}

interface Override {
  email: string;
  claude_usd: number | null;
  codex_usd: number | null;
  note: string;
  updated_by: string;
  updated_at: string | null;
}

interface KindStatus {
  spend_usd: number;
  allowance_usd: number;
  level: number;
  blocked: boolean;
}

interface Person {
  email: string;
  tier: string;
  claude: KindStatus | null;
  codex: KindStatus | null;
}

interface DirectoryEntry {
  email: string;
  tier: string;
}

interface AllowancesReport {
  month: string;
  config: Config;
  overrides: Override[];
  people: Person[];
  directory: DirectoryEntry[];
  directory_error: string | null;
}

const MODE_LABEL: Record<Mode, string> = {
  off: 'Off',
  dry_run: 'Dry run: shows who would be blocked, blocks no one',
  on: 'On: blocks at 100%',
};

const usd = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function fmtTs(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

function amount(value: string): number | null {
  return value.trim() === '' ? null : Number(value);
}

function Status({ status, mode }: { status: KindStatus | null; mode: Mode }): ReactNode {
  if (!status) return <span className="text-steel-400">—</span>;
  const pct = status.allowance_usd > 0 ? (status.spend_usd / status.allowance_usd) * 100 : 100;
  const label = status.blocked
    ? 'Blocked'
    : status.level === 100
      ? mode === 'on'
        ? 'Over'
        : 'Over (not blocked)'
      : status.level > 0
        ? `${status.level}%`
        : 'OK';
  const tone = status.blocked || status.level === 100
    ? 'bg-red-100 text-red-800'
    : status.level === 80
      ? 'bg-amber-100 text-amber-800'
      : status.level === 50
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-steel-100 text-steel-700';
  return (
    <div className="min-w-40">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span>
          {usd(status.spend_usd)} of {usd(status.allowance_usd)}
        </span>
        <span className={`rounded px-1.5 py-0.5 font-medium ${tone}`}>{label}</span>
      </div>
      <div className="mt-1 h-1.5 rounded bg-steel-100">
        <div
          className={`h-1.5 rounded ${status.level >= 80 ? 'bg-red-500' : 'bg-navy-500'}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

function ConfigForm({ config }: { config: Config }): ReactNode {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>(config.mode);
  const [opus, setOpus] = useState(String(config.opus_claude_usd));
  const [sonnet, setSonnet] = useState(String(config.sonnet_claude_usd));
  const [codex, setCodex] = useState(String(config.codex_usd));
  const [emails, setEmails] = useState(config.alert_emails);
  const save = useMutation({
    mutationFn: () =>
      api.put<Config>('/admin/allowances/config', {
        mode,
        opus_claude_usd: Number(opus),
        sonnet_claude_usd: Number(sonnet),
        codex_usd: Number(codex),
        alert_emails: emails,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-allowances'] }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const field = 'w-28 rounded border border-steel-300 px-2 py-1 text-sm';
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col text-xs text-steel-600">
        Switch
        <select
          aria-label="Allowance switch"
          value={mode}
          onChange={(e) => setMode(e.target.value as Mode)}
          className="mt-1 rounded border border-steel-300 px-2 py-1 text-sm"
        >
          {(Object.keys(MODE_LABEL) as Mode[]).map((m) => (
            <option key={m} value={m}>
              {MODE_LABEL[m]}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col text-xs text-steel-600">
        Opus tier, Claude ($/month)
        <input aria-label="Opus tier" type="number" min="0" step="0.01" value={opus}
          onChange={(e) => setOpus(e.target.value)} className={`mt-1 ${field}`} />
      </label>
      <label className="flex flex-col text-xs text-steel-600">
        Sonnet tier, Claude ($/month)
        <input aria-label="Sonnet tier" type="number" min="0" step="0.01" value={sonnet}
          onChange={(e) => setSonnet(e.target.value)} className={`mt-1 ${field}`} />
      </label>
      <label className="flex flex-col text-xs text-steel-600">
        Codex ($/month)
        <input aria-label="Codex" type="number" min="0" step="0.01" value={codex}
          onChange={(e) => setCodex(e.target.value)} className={`mt-1 ${field}`} />
      </label>
      <label className="flex items-center gap-2 self-center text-sm text-steel-700">
        <input type="checkbox" aria-label="Alert emails" checked={emails}
          onChange={(e) => setEmails(e.target.checked)} />
        Email each person at 50%, 80% and when paused (only when the switch is On)
      </label>
      <button
        type="submit"
        disabled={save.isPending}
        className="rounded bg-navy-800 px-3 py-1 text-sm font-medium text-white disabled:opacity-50"
      >
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
      {save.error && (
        <p role="alert" className="w-full text-sm text-red-700">
          {save.error instanceof Error ? save.error.message : 'Could not save.'}
        </p>
      )}
    </form>
  );
}

function TestEmail(): ReactNode {
  const [to, setTo] = useState('');
  const send = useMutation({
    mutationFn: () =>
      api.post<{ sent_to: string }>('/admin/allowances/test-email', to.trim() ? { to: to.trim() } : {}),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };
  return (
    <form onSubmit={submit} className="mt-3 flex flex-wrap items-center gap-2 text-sm">
      <input
        aria-label="Test email address"
        type="email"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        placeholder="Your own address"
        className="w-64 rounded border border-steel-300 px-2 py-1 text-sm"
      />
      <button
        type="submit"
        disabled={send.isPending}
        className="rounded border border-steel-300 px-2 py-1 text-sm text-navy-800 hover:border-navy-400 disabled:opacity-50"
      >
        {send.isPending ? 'Sending…' : 'Send a sample alert email'}
      </button>
      {send.data && <span className="text-steel-600">Sent to {send.data.sent_to}.</span>}
      {send.error && (
        <span role="alert" className="text-red-700">
          {send.error instanceof Error ? send.error.message : 'Could not send.'}
        </span>
      )}
    </form>
  );
}

function Overrides({
  overrides,
  directory,
  config,
}: {
  overrides: Override[];
  directory: DirectoryEntry[];
  config: Config;
}): ReactNode {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [claude, setClaude] = useState('');
  const [codex, setCodex] = useState('');
  const [note, setNote] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: ['admin-allowances'] });
  const save = useMutation({
    mutationFn: () =>
      api.put<Override>(`/admin/allowances/overrides/${encodeURIComponent(email.trim())}`, {
        claude_usd: amount(claude),
        codex_usd: amount(codex),
        note,
      }),
    onSuccess: () => {
      setEmail('');
      setClaude('');
      setCodex('');
      setNote('');
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (target: string) =>
      api.delete<void>(`/admin/allowances/overrides/${encodeURIComponent(target)}`),
    onSuccess: refresh,
  });
  const ready = email.trim() !== '' && note.trim() !== '' && (claude !== '' || codex !== '');
  const picked = directory.find((d) => d.email === email.trim().toLowerCase());
  const tierClaude = picked?.tier === 'opus' ? config.opus_claude_usd : config.sonnet_claude_usd;
  // A blank column keeps the tier amount; show the amount rather than the word "tier".
  const claudeFor = (who: string) => {
    const tier = directory.find((d) => d.email === who)?.tier;
    if (!tier) return null;
    return tier === 'opus' ? config.opus_claude_usd : config.sonnet_claude_usd;
  };
  const standard = (amount: number | null) => (
    <span className="text-steel-500">{amount === null ? 'standard' : `${usd(amount)} (standard)`}</span>
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) save.mutate();
  };
  const error = save.error ?? remove.error;
  const input = 'rounded border border-steel-300 px-2 py-1 text-sm';
  return (
    <div>
      <SubHeading
        title="Per-person allowances"
        note="Replaces the tier amount for one person. Leave Claude or Codex blank to keep the tier amount."
      />
      <form onSubmit={submit} className="mb-3 flex flex-wrap gap-2">
        <input aria-label="Override person" type="search" inputMode="email"
          name="allowance-person" placeholder="Search people" list="allowance-people"
          autoComplete="off"
          value={email} onChange={(e) => setEmail(e.target.value)} className={`min-w-56 flex-1 ${input}`} />
        <datalist id="allowance-people">
          {directory.map((d) => (
            <option key={d.email} value={d.email} label={d.tier === 'opus' ? 'Opus tier' : 'Sonnet tier'} />
          ))}
        </datalist>
        <input aria-label="Claude allowance" type="number" min="0" step="0.01" placeholder="Claude $"
          value={claude} onChange={(e) => setClaude(e.target.value)} className={`w-28 ${input}`} />
        <input aria-label="Codex allowance" type="number" min="0" step="0.01" placeholder="Codex $"
          value={codex} onChange={(e) => setCodex(e.target.value)} className={`w-28 ${input}`} />
        <input aria-label="Override note" placeholder="Reason (who approved it)" value={note}
          onChange={(e) => setNote(e.target.value)} className={`min-w-56 flex-1 ${input}`} />
        <button
          type="submit"
          disabled={!ready || save.isPending}
          className="rounded bg-navy-800 px-3 py-1 text-sm font-medium text-white disabled:opacity-50"
        >
          Set
        </button>
      </form>
      {picked && (
        <p className="-mt-1 mb-3 text-xs text-steel-600" data-testid="override-tier">
          {picked.tier === 'opus' ? 'Opus' : 'Sonnet'} tier: Claude {usd(tierClaude)}, Codex{' '}
          {usd(config.codex_usd)} a month unless you set otherwise.
        </p>
      )}
      {error && (
        <p role="alert" className="mb-2 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Something went wrong.'}
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-steel-200">
        <table className="w-full text-sm">
          <thead className="bg-steel-50 text-left text-[12px] uppercase tracking-wide text-steel-500">
            <tr>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2 text-right">Claude</th>
              <th className="px-3 py-2 text-right">Codex</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Set by</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {overrides.map((o) => (
              <tr key={o.email} className="border-t border-steel-100">
                <td className="px-3 py-2 font-medium text-navy-900">{o.email}</td>
                <td className="px-3 py-2 text-right">{o.claude_usd === null ? standard(claudeFor(o.email)) : usd(o.claude_usd)}</td>
                <td className="px-3 py-2 text-right">{o.codex_usd === null ? standard(config.codex_usd) : usd(o.codex_usd)}</td>
                <td className="px-3 py-2 text-steel-700">{o.note}</td>
                <td className="px-3 py-2 text-steel-600">{o.updated_by}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => remove.mutate(o.email)}
                    disabled={remove.isPending}
                    className="rounded border border-steel-300 px-2 py-0.5 text-xs text-navy-800 hover:border-navy-400"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {overrides.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-steel-500" colSpan={6}>
                  Everyone is on their tier's allowance.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Allowances(): ReactNode {
  const qc = useQueryClient();
  const report = useQuery({
    queryKey: ['admin-allowances'],
    queryFn: () => api.get<AllowancesReport>('/admin/allowances'),
  });
  const run = useMutation({
    mutationFn: () => api.post<unknown>('/admin/allowances/run'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-allowances'] }),
  });
  const data = report.data;
  const config = data?.config;
  const forbidden = report.error instanceof ApiError && report.error.status === 403;
  const people = [...(data?.people ?? [])].sort((a, b) => {
    const worst = (p: Person) =>
      Math.max(p.claude ? p.claude.spend_usd / (p.claude.allowance_usd || 1e-9) : 0,
        p.codex ? p.codex.spend_usd / (p.codex.allowance_usd || 1e-9) : 0);
    return worst(b) - worst(a);
  });

  return (
    <Panel
      testId="allowances"
      title="Monthly allowances"
      description="Each person's spend this month against their allowance: the AWS bill (updated daily) plus portal use, which counts toward Claude. Alerts at 50% and 80%. At 100%, Claude (Desktop, Cowork and the portal) or Codex is blocked until the 1st. Checked hourly."
      actions={
        <button
          type="button"
          disabled={run.isPending}
          onClick={() => run.mutate()}
          className="rounded border border-steel-300 px-2 py-0.5 text-xs text-navy-800 hover:border-navy-400 disabled:opacity-50"
        >
          {run.isPending ? 'Checking…' : 'Run check now'}
        </button>
      }
    >
      {report.isLoading && <p className="text-sm text-steel-500">Loading…</p>}
      {forbidden && (
        <p role="alert" className="text-sm text-amber-900">
          Allowances are restricted to platform administrators.
        </p>
      )}
      {report.error && !forbidden && (
        <p role="alert" className="text-sm text-red-700">
          {report.error instanceof Error ? report.error.message : 'Could not load allowances.'}
        </p>
      )}
      {run.error && (
        <p role="alert" className="mb-2 text-sm text-red-700">
          {run.error instanceof Error ? run.error.message : 'Check failed.'}
        </p>
      )}
      {data && config && (
        <div className="flex flex-col gap-6">
          <div>
            <SubHeading
              title="Settings"
              note={`Last changed by ${config.updated_by} ${fmtTs(config.updated_at)}. Last check: ${fmtTs(config.last_run_at)}.`}
            />
            {config.last_run_error && (
              <p role="alert" className="mb-2 text-sm text-red-700">
                Last check failed: {config.last_run_error}
              </p>
            )}
            <ConfigForm key={`${config.updated_at}`} config={config} />
            <TestEmail />
          </div>

          <div className="border-t border-steel-100 pt-5">
            <SubHeading title={`This month (${data.month})`} />
            <div className="overflow-x-auto rounded-lg border border-steel-200">
              <table className="w-full text-sm">
                <thead className="bg-steel-50 text-left text-[12px] uppercase tracking-wide text-steel-500">
                  <tr>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Tier</th>
                    <th className="px-3 py-2">Claude (Desktop, Cowork, portal)</th>
                    <th className="px-3 py-2">Codex</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.email} className="border-t border-steel-100">
                      <td className="px-3 py-2 font-medium text-navy-900">{p.email}</td>
                      <td className="px-3 py-2 capitalize text-steel-700">{p.tier}</td>
                      <td className="px-3 py-2"><Status status={p.claude} mode={config.mode} /></td>
                      <td className="px-3 py-2"><Status status={p.codex} mode={config.mode} /></td>
                    </tr>
                  ))}
                  {people.length === 0 && (
                    <tr>
                      <td className="px-3 py-3 text-steel-500" colSpan={4}>
                        No spend recorded this month yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border-t border-steel-100 pt-5">
            {data.directory_error && (
              <p className="mb-2 text-xs text-amber-800">
                Couldn't load everyone from Identity Center, so the list shows this month's
                people only. You can still type any email.
              </p>
            )}
            <Overrides overrides={data.overrides} directory={data.directory} config={config} />
          </div>
        </div>
      )}
    </Panel>
  );
}

export function AllowancesPage(): ReactNode {
  return (
    <div className="flex flex-col gap-6 animate-fade-in" data-testid="allowances-page">
      <header>
        <h2 className="text-xl font-semibold text-navy-900">Allowances</h2>
        <p className="mt-1 text-sm text-steel-600">
          Monthly spend limits for Claude and Codex, per person. For who used which model, see{' '}
          <Link to="/dashboard" className="text-navy-700 underline">
            Usage by person
          </Link>{' '}
          on the Dashboard.
        </p>
      </header>
      <Allowances />
    </div>
  );
}
